import React, { useEffect, useMemo, useState } from "react";
import { Column, useSortBy, useTable } from "react-table";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MemberServices from "../../services/MemberServices";
import CommitteeServices from "../../services/CommitteeServices";
import { Button, Label } from "../../components/StyledComp/StyledComp";
import "../../Styles/CommonStyles.scss";

type CommitteeType = "committee" | "subcommittee";

interface ISection {
  Id: number;
  SectionName: string;
}

interface ICommittee {
  Id: number;
  SectionId: number;
  SectionName: string;
  Name: string;
  Type: CommitteeType;
  Status: number;
}

const CommitteesPage: React.FC = () => {
  const [sections, setSections] = useState<ISection[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
  const [committees, setCommittees] = useState<ICommittee[]>([]);
  const [newCommitteeName, setNewCommitteeName] = useState<string>("");
  const [newCommitteeType, setNewCommitteeType] = useState<CommitteeType>("committee");
  const [editingCommittee, setEditingCommittee] = useState<ICommittee | null>(null);
  const [editName, setEditName] = useState<string>("");
  const [editType, setEditType] = useState<CommitteeType>("committee");
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const loadSections = async () => {
      try {
        const response = await MemberServices.getConfigurationList();
        if (response.data?.data?.MemberSection) {
          setSections(response.data.data.MemberSection);
        }
      } catch (err) {
        alert("Failed to load sections.");
      }
    };
    loadSections();
  }, []);

  const loadCommittees = async (sectionId: number) => {
    setLoading(true);
    try {
      const response = await CommitteeServices.getCommittees(sectionId);
      if (response.data?.data != null) {
        setCommittees(response.data.data);
      } else {
        setCommittees([]);
      }
    } catch (err) {
      alert("Failed to load committees.");
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = parseInt(e.target.value, 10);
    setSelectedSectionId(id || null);
    setNewCommitteeName("");
    setNewCommitteeType("committee");
    setEditingCommittee(null);
    if (id) loadCommittees(id);
    else setCommittees([]);
  };

  const handleAddCommittee = async () => {
    if (!selectedSectionId || !newCommitteeName.trim()) return;
    try {
      const response = await CommitteeServices.createCommittee({
        SectionId: selectedSectionId,
        Name: newCommitteeName.trim(),
        Type: newCommitteeType,
      });
      if (response.data?.data != null) {
        setNewCommitteeName("");
        setNewCommitteeType("committee");
        loadCommittees(selectedSectionId);
      } else {
        alert(response.data?.errors?.[0]?.message || "Failed to create committee.");
      }
    } catch (err) {
      alert("Failed to create committee.");
    }
  };

  const handleDeleteCommittee = async (committee: ICommittee) => {
    if (
      !window.confirm(
        `Delete "${committee.Name}"? This cannot be done if members are currently assigned as chairs.`
      )
    )
      return;
    try {
      const response = await CommitteeServices.deleteCommittee(committee.Id);
      if (response.data?.status) {
        loadCommittees(selectedSectionId!);
      } else {
        const msg = response.data?.errors?.[0]?.message || "Failed to delete committee.";
        alert(msg);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0]?.message || "Failed to delete committee.";
      alert(msg);
    }
  };

  const handleStartEdit = (committee: ICommittee) => {
    setEditingCommittee(committee);
    setEditName(committee.Name);
    setEditType(committee.Type);
  };

  const handleSaveEdit = async () => {
    if (!editingCommittee || !editName.trim()) return;
    try {
      const response = await CommitteeServices.updateCommittee(editingCommittee.Id, {
        Name: editName.trim(),
        Type: editType,
      });
      if (response.data?.status) {
        setEditingCommittee(null);
        loadCommittees(selectedSectionId!);
      } else {
        alert("Failed to update committee.");
      }
    } catch (err) {
      alert("Failed to update committee.");
    }
  };

  const columns = useMemo<Column<ICommittee>[]>(
    () => [
      { id: "Name", Header: "Name", accessor: "Name" },
      {
        id: "Type",
        Header: "Type",
        accessor: (row: ICommittee) => (row.Type === "subcommittee" ? "Subcommittee" : "Committee"),
      },
      {
        id: "actions",
        Header: "Actions",
        accessor: (row: ICommittee) => row,
        Cell: ({ value: committee }: { value: ICommittee }) => (
          <div style={{ display: "flex", gap: "12px" }}>
            <a
              style={{ cursor: "pointer", color: "#1976d2" }}
              onClick={() => handleStartEdit(committee)}
            >
              <EditIcon fontSize="small" />
            </a>
            <a
              style={{ cursor: "pointer", color: "#d32f2f" }}
              onClick={() => handleDeleteCommittee(committee)}
            >
              <DeleteIcon fontSize="small" />
            </a>
          </div>
        ),
      },
    ],
    [selectedSectionId]
  );

  const tableData = useMemo(() => committees, [committees]);

  const tableInstance = useTable({ columns, data: tableData }, useSortBy);
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  const selectedSectionName = sections.find((s) => s.Id === selectedSectionId)?.SectionName;

  return (
    <div className="dashboard-wrapper" style={{ padding: "24px", alignItems: "flex-start" }}>
      <h1 className="page-heading">Section Committees</h1>
      <p style={{ marginBottom: "20px", color: "#555" }}>
        Create and manage committees and subcommittees for each section. Once created,
        chairs can be assigned from the member detail page.
      </p>

      {/* Section selector */}
      <div style={{ marginBottom: "24px", maxWidth: "400px" }}>
        <Label>Select Section</Label>
        <select
          className="input-field-div"
          style={{ width: "100%", marginTop: "6px" }}
          value={selectedSectionId ?? ""}
          onChange={handleSectionChange}
        >
          <option value="">-- Select a Section --</option>
          {sections.map((section) => (
            <option key={section.Id} value={section.Id}>
              {section.SectionName}
            </option>
          ))}
        </select>
      </div>

      {selectedSectionId && (
        <>
          <h2 style={{ fontSize: "1.1rem", fontWeight: 600, marginBottom: "12px" }}>
            Committees for: {selectedSectionName}
          </h2>

          {/* Add committee form */}
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              marginBottom: "20px",
              maxWidth: "500px",
              width: "100%",
              flexDirection: "column",
            }}
          >
            <input
              className="input-field-div"
              style={{ flex: 1 }}
              placeholder="New committee or subcommittee name (e.g. CARE, UCC)"
              value={newCommitteeName}
              onChange={(e) => setNewCommitteeName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleAddCommittee(); }}
            />
            <select
              className="input-field-div"
              style={{ width: "100%" }}
              value={newCommitteeType}
              onChange={(e) => setNewCommitteeType(e.target.value as CommitteeType)}
            >
              <option value="committee">Committee</option>
              <option value="subcommittee">Subcommittee</option>
            </select>
            <Button
              type="button"
              onClick={handleAddCommittee}
              disabled={!newCommitteeName.trim()}
            >
              Add
            </Button>
          </div>

          {/* Inline edit form */}
          {editingCommittee && (
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                marginBottom: "20px",
                maxWidth: "500px",
                width: "100%",
                padding: "12px",
                background: "#f5f5f5",
                borderRadius: "6px",
                flexDirection: "column",
              }}
            >
              <span style={{ whiteSpace: "nowrap", fontSize: "0.9rem" }}>Editing:</span>
              <input
                className="input-field-div"
                style={{ flex: 1 }}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSaveEdit(); }}
                autoFocus
              />
              <select
                className="input-field-div"
                style={{ width: "100%" }}
                value={editType}
                onChange={(e) => setEditType(e.target.value as CommitteeType)}
              >
                <option value="committee">Committee</option>
                <option value="subcommittee">Subcommittee</option>
              </select>
              <Button type="button" onClick={handleSaveEdit} disabled={!editName.trim()}>
                Save
              </Button>
              <button
                type="button"
                onClick={() => setEditingCommittee(null)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#555" }}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Committees table */}
          {loading ? (
            <p>Loading...</p>
          ) : committees.length === 0 ? (
            <p style={{ color: "#888" }}>No committees yet. Add one above.</p>
          ) : (
            <table {...getTableProps()} className="table" style={{ maxWidth: "600px" }}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps((column as any).getSortByToggleProps())}
                        style={{ textAlign: "left", padding: "8px 12px", borderBottom: "2px solid #ddd" }}
                      >
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} style={{ borderBottom: "1px solid #eee" }}>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          style={{ padding: "8px 12px" }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default CommitteesPage;
